import {
  Box,
  Button,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { InventoryItemsTable } from "components/Inventory/InventoryItemsTable";
import { useSnackbar } from "notistack";
import { useGetItemsDropdownService } from "subModule/src/services/items/useItems";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useNavigate, useParams } from "react-router-dom";
import { InventoryProfileService } from "subModule/src/core/services";
import {
  GetCreditNoteResponse,
  NewPurchaseOrderResponse,
  SaveCreditNote,
} from "subModule/src/services/inventory/interface";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import { DeleteData } from "subModule/src/type";
import { useGetServicesDropdownService } from "subModule/src/services/service/useService";
import { InventoryServicesTable } from "components/Inventory/InventoryServicesTable";
import BasicDatePicker from "common/Pickers/Pickers";
import DynamicSelect from "common/Select/Select";
import { useGetStockReceivedApprovedService } from "subModule/src/services/inventory/useInventory";
import dayjs from "dayjs";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import FormDialog from "common/Modal/Modal";
import ServicesModal from "pages/Service/ServicesModal";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { GetItemsDropdownResponse } from "subModule/src/services/items/interface";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";

interface FormData {
  [key: string]: any;
}

const CreditNoteCreateUpdate: React.FC = () => {
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "vendors",
    moduleLabelKey: "Debit Notes",
    moduleHref: "/inventory/debit-note",
    entityFallbackLabelKey: "Debit Note",
    isEdit: !!params?.id,
    action: "details",
  });
  const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    resetField,
    reset,
  } = useForm<FormData>({
    defaultValues: {
      pCDNoteItems: [],
      pCDNoteService: [],
      creditDebitDate: dayjs(new Date())?.format("YYYY-MM-DD"),
    },
  });
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>('');
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const { data: itemsData, refetch: itemsRefetch } = useGetItemsDropdownService({
    pageNumber: dropdownPage,
    size: 20,
    storeId: watch('storeId')?.value,
    search: dropdownSearchTerm
  });
  const { data: servicesData, refetch: servicesRefetch } = useGetServicesDropdownService(
    { pageNumber: dropdownPage, size: 20, search: dropdownSearchTerm, }
  );
  const { data: invoiceData, refetch: invoicesRefetch } = useGetStockReceivedApprovedService({
    pageNumber: dropdownPage,
    size: 20,
    type: 3,
    invoiceNumber: dropdownSearchTerm
  });
  const [originalInvoiceItems, setOriginalInvoiceItems] = useState<any[]>([]);
  const [pCDNoteItems, setpCDNoteItems] = useState<any[]>([]);
  const [pCDNoteService, setpCDNoteService] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [creditnote, setCreditNote] = useState<SaveCreditNote>();
  const [taxPercentage, setTaxPercentage] = useState<number>(0);
  const [addNew, setAddNew] = useState<string>("");
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(false);
  const [confirm, setConfirm] = useState<boolean>(false);
  const invoiceOptions = (invoiceData?.pages[0]?.data?.content || [])?.map(
    (item: any) => ({ value: item.id, label: item.invoiceNo })
  );
  useEffect(() => {
    servicesRefetch();
    invoicesRefetch();
    if (params?.id) {
      setTimeout(() => setLoading(true), 50);
      getCreditNoteByID();
    }
  }, [params?.id]);
  useEffect(() => {
    if (watch('storeId')?.value) {
      setLoading(true);
      getParts();
    }
  }, [watch('storeId')?.value]);
  const getParts = () => {
    itemsRefetch().then((res: GetItemsDropdownResponse | unknown | any) => {
      if (res?.data?.pages[0]?.success) {
        ///
      } else {
        enqueueSnackbar(<AlertMessages text={res?.data?.message || "Something went wrong"} />, { variant: "error" });
      }
    }).catch((err) => {
      console.error("===", err);
    }).finally(() => setLoading(false));
  }
  const getCreditNoteByID = () => {
    InventoryProfileService.getEntityById(params?.id, "purchasecreditdebitnote")
      .then((res: any) => {
        if (res.success) {
          setCreditNote(res.data as any);
          captureLabel(
            (res.data as any)?.creditDebitNoteNumber != null
              ? String((res.data as any).creditDebitNoteNumber)
              : null,
          );
          setpCDNoteItems((res.data as any)?.pcdnoteItemResponse || []);
          setValue("pCDNoteItems", res.data.pcdnoteItemResponse || []);
          setpCDNoteService((res.data as any)?.pcdnoteServiceResponse || []);
          setValue("pCDNoteService", res.data?.pcdnoteServiceResponse || []);
          setValue("type", 6);
          setValue("poId", null);
          setValue("purchaseInvoiceId", {
            value: res?.data?.purchaseInvoiceId,
            label: res?.data?.purchaseInvoiceNumber || res?.data?.purchaseInvoiceId,
          });
          setValue("storeId", res.data?.storeId);
          setValue("supplierId", res.data?.supplierId);
          setValue("adjustmentType", 1);
          setValue("referenceNumber", res.data?.referenceNumber);
          setValue("creditDebitDate", res.data?.creditDebitDate?.split("T")[0]);
          setTaxPercentage(((res.data as any)?.pcdnoteItemResponse?.[0]?.taxPercentage || (res.data as any)?.pcdnoteServiceResponse?.[0]?.taxPercentage || 0));
          if(!params?.id) {
            getParts();
            setLoading(true);
          } else setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" }
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setLoading(false);
      });
  };
  const getInvoiceById = (id: string | undefined) => {
    InventoryProfileService.getInvoiceById(id, 3)
      .then((res: any) => {
        if (res.success) {
          setCreditNote(res.data);
          setOriginalInvoiceItems(res.data?.purchaseInvoiceItem || []);
          setpCDNoteItems(res.data?.purchaseInvoiceItem || []);
          setValue("pCDNoteItems", res.data.purchaseInvoiceItem || []);
          setpCDNoteService(res.data?.purchaseInvoiceService || []);
          setValue("pCDNoteService", res.data?.purchaseInvoiceService || []);
          setTaxPercentage(((res.data as any)?.purchaseInvoiceItem?.[0]?.taxPercentage || (res.data as any)?.purchaseInvoiceService?.[0]?.taxPercentage || 0));
          setValue("storeId", {
            value: res.data?.storeId,
            label: res.data?.storeTitle,
          });
          setValue("supplierId", {
            value: res.data?.supplierId,
            label: res.data?.vendorTitle,
          });
          setValue("reason", res.data?.comments);
          setValue("poId", res.data?.poId || null);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" }
          );
          setLoading(false);
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setLoading(false);
      });
  };
  const columns = [
    { id: "itemId" },
    { id: "itemCode" },
    { id: "unitId" },
    {
      id: "itemName",
      label: t("Item") + " *",
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "supplierSKU",
      label: t("SKU"),
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "quantity",
      label: t("Quantity"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    { id: "unitTitle" },
    {
      id: "unitPrice",
      label: t("Cost"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "discount",
      label: `${t("Discount")} %`,
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    { id: "total", label: t("Total") },
  ];
  const columnsService = [
    { id: "serviceId" },
    { id: "serviceCode" },
    {
      id: "serviceName",
      label: t("service") + " *",
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "quantity",
      label: t("Quantity"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "unitPrice",
      label: t("Cost"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "discount",
      label: `${t("Discount")} %`,
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    { id: "total", label: t("Total") },
  ];
  const DeleteItem = (row: any, typeName: string) => {
    if (typeName === "pCDNoteItems" && params?.id) {
      setLoading(true);
      InventoryProfileService.deleteEntityItem(
        (row as any).id,
        "purchasecreditdebitnote/item"
      )
        .then((response: DeleteData) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={t("deleted") + " " + t("successfully")}
              />,
              { variant: "success" }
            );
          } else
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          setLoading(false);
          getCreditNoteByID();
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" }
          );
          setLoading(false);
        });
    } else if (typeName === "pCDNoteItems" && !params?.id) {
      setpCDNoteItems(prevRows => {
        const newRows = prevRows.filter((rows) => rows.id !== row.id);
        setValue('pCDNoteItems', []);
        setTimeout(() => setValue('pCDNoteItems', newRows, { shouldValidate: true, shouldDirty: true }), 0);
        return newRows;
      });
    }
    if (typeName === "pCDNoteService" && params?.id) {
      setLoading(true);
      InventoryProfileService.deleteEntityItem(
        (row as any).id,
        "purchasecreditdebitnote/service"
      )
        .then((response: DeleteData) => {
          if (response.success)
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={t("deleted") + " " + t("successfully")}
              />,
              { variant: "success" }
            );
          else
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          setLoading(false);
          getCreditNoteByID();
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" }
          );
          setLoading(false);
        });
    } else if (typeName === "pCDNoteService" && !params?.id) {
      setpCDNoteService(prevRows => {
        const newRows = prevRows.filter((rows) => rows.id !== row.id);
        setValue('pCDNoteService', []);
        setTimeout(() => setValue('pCDNoteService', newRows, { shouldValidate: true, shouldDirty: true }), 0);
        return newRows;
      });
    }
  };
  const addNewItem = (typeName: string) => {
    if (typeName === "pCDNoteItems") {
      setValue('pCDNoteItems', originalInvoiceItems);
      setpCDNoteItems(originalInvoiceItems);
    } else {
      const newService = columnsService.reduce(
        (acc, column) => ({
          ...acc,
          [column.id]: column.type === "number" ? 0 : "",
        }),
        {}
      );
      if (!params?.id) (newService as any).id = Date.now();
      setpCDNoteService((prevRows) => [...prevRows, newService]);
    }
  };
  const handleItem = (row: any, typeName: string, action: string) => {
    if (typeName === "pCDNoteItems" && params?.id) {
      if (action === "create") {
        InventoryProfileService.createCreditNoteItem({
          ...row,
          poId: params?.id || null,
          purchaseCreditDebitNoteId: creditnote?.id,
          storeId: creditnote?.storeId,
        })
          .then((response: GetCreditNoteResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("creditNoteItem") +
                    " " +
                    t("created") +
                    " " +
                    t("successfully")
                  }
                />,
                { variant: "success" }
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getCreditNoteByID();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" }
            );
            getCreditNoteByID();
          });
      } else {
        InventoryProfileService.updateCreditNoteItem({
          ...row,
          poId: params?.id || null,
          purchaseCreditDebitNoteId: creditnote?.id,
        })
          .then((response: GetCreditNoteResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("creditNoteItem") +
                    " " +
                    t("updated") +
                    " " +
                    t("successfully")
                  }
                />,
                { variant: "success" }
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getCreditNoteByID();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" }
            );
            getCreditNoteByID();
          });
      }
    } else if (typeName === "pCDNoteService" && params?.id) {
      if (action === "create") {
        InventoryProfileService.createCreditNoteService({
          ...row,
          poId: params?.id || null,
          purchaseCreditDebitNoteId: creditnote?.id,
          storeId: creditnote?.storeId,
        })
          .then((response: GetCreditNoteResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("creditNoteService") +
                    " " +
                    t("created") +
                    " " +
                    t("successfully")
                  }
                />,
                { variant: "success" }
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getCreditNoteByID();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" }
            );
            getCreditNoteByID();
          });
      } else {
        InventoryProfileService.updateCreditNoteService({
          ...row,
          poId: params?.id || null,
          purchaseCreditDebitNoteId: creditnote?.id,
          storeId: creditnote?.storeId,
        })
          .then((response: GetCreditNoteResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("creditNoteService") +
                    " " +
                    t("updated") +
                    " " +
                    t("successfully")
                  }
                />,
                { variant: "success" }
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getCreditNoteByID();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" }
            );
            getCreditNoteByID();
          });
      }
    }
  };

  const handleInputChange = (
    index: number,
    columnId: string,
    value: any,
    typeName: string,
    type: string
  ) => {
    if (typeName === "pCDNoteItems") {
      const updatedItems = [...pCDNoteItems];
      updatedItems[index][columnId] =
        type === "number" ? parseFloat(value) : value;
      setpCDNoteItems(updatedItems);
    } else {
      const updatedServices = [...pCDNoteService];
      updatedServices[index][columnId] = value;
      setpCDNoteService(updatedServices);
    }
  };
  const onSubmit = (data: FormData | SaveCreditNote) => {
    if (Object.values(data)?.length) {
      setIsBtnEnabled(true);
      const filteredItems = pCDNoteItems.filter((item: any) => item?.itemName);
      const filteredServices = pCDNoteService.filter(
        (service: any) => service?.serviceName
      );
      if (filteredItems.length === 0) {
        enqueueSnackbar(<AlertMessages text={t("addAtLeastOneItem")} />, {
          variant: "error",
        });
        setIsBtnEnabled(false);
        return;
      }
      filteredItems.every(
        (row: any) => delete row.total && delete row.id && row.unitTitle
      );
      filteredServices.every((row: any) => delete row.total && delete row.id);
      InventoryProfileService.saveCreditNote({
        ...data,
        type: 6,
        storeId: data.storeId.value,
        supplierId: data.supplierId.value,
        purchaseInvoiceId: data?.purchaseInvoiceId?.value || null,
        pCDNoteItems: filteredItems?.length
          ? filteredItems.map((row: any) => ({
            ...row,
            purchaseInvoiceId: null,
          }))
          : [],
        pCDNoteService: filteredServices?.length
          ? filteredServices.map((row: any) => ({
            ...row,
            purchaseInvoiceId: null,
            serviceId: typeof row.serviceId === "string" ? null : row.serviceId
          }))
          : [],
      })
        .then((response: GetCreditNoteResponse) => {
          if (response.success) {
            enqueueSnackbar(<AlertMessages statusCode={response.status} text={ t("Debit Note") + " " + t("saved") + " " + t("successfully") } />, { variant: "success" });
            navigate(`/inventory/debit-note/${response.data.id}`);
          } else
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          setLoading(false);
        })
        .catch((err: any) => {
          console.error("===", err);
          setLoading(false);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" }
          );
        }).finally(() => setIsBtnEnabled(false));
    }
  };
  const calculateTotals = () => {
    const itemTotals = pCDNoteItems.reduce(
      (acc, item) => {
        const unitPrice = parseFloat(item.unitPrice || 0);
        const quantity = parseFloat(item.quantity || 0);
        const discountPercentage = parseFloat(item.discount || 0);
        const discountAmount = (unitPrice * discountPercentage) / 100;
        const totalAfterDiscount = (unitPrice - discountAmount) * quantity;

        return {
          unitPrice: parseFloat(acc.unitPrice) + unitPrice,
          discount: parseFloat(acc.discount) + (discountAmount * quantity),
          subTotal: parseFloat(acc.subTotal) + (unitPrice * quantity),
          total: parseFloat(acc.total) + totalAfterDiscount,
        };
      },
      { unitPrice: 0, discount: 0, subTotal: 0, total: 0 }
    );

    const serviceTotals = pCDNoteService?.reduce(
      (acc, service) => {
        const unitPrice = parseFloat(service.unitPrice || 0);
        const quantity = parseFloat(service.quantity || 0);
        const discountPercentage = parseFloat(service.discount || 0);
        const discountAmount = (unitPrice * discountPercentage) / 100;
        const totalAfterDiscount = (unitPrice - discountAmount) * quantity;
        return {
          unitPrice: parseFloat(acc?.unitPrice) + unitPrice,
          discount: parseFloat(acc?.discount) + (discountAmount * quantity),
          subTotal: parseFloat(acc?.subTotal) + (unitPrice * quantity),
          total: parseFloat(acc?.total) + totalAfterDiscount,
        };
      },
      { unitPrice: 0, discount: 0, subTotal: 0, total: 0 }
    );

    return {
      unitPrice: itemTotals.unitPrice + serviceTotals?.unitPrice,
      discount: itemTotals.discount + serviceTotals?.discount,
      subTotal: itemTotals.subTotal + serviceTotals?.subTotal,
      taxableTotal: itemTotals.total,
      total: itemTotals.total + serviceTotals?.total,
    };
  };
  const totals = calculateTotals();
  const handleDropdownActions = (typeName: string, action: 'searchIt' | 'loadNext', search?: string, table?: string) => {
    if(action === 'searchIt') {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if(search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if(table === 'inventoryServices') {
        servicesRefetch().finally(() => setSearchLoading(null));
        return;
      }
      if(typeName === 'purchaseInvoices') invoicesRefetch().finally(() => setSearchLoading(null));
      else itemsRefetch().finally(() => setSearchLoading(null));
    }, 300);
  }
  return (
    <>
    <ComponentHeaderTwo
      breadcrumbItems={breadcrumbItems}
      showBtn={true}
      showSaveBtn={false}
      onClick={() => navigate("/inventory/debit-note")}
      onSubmit={() => {}}
      heading={t("Debit Notes")}
      icon={
        <InventoryIcon
          sx={{ fill: "#000", width: "28px", height: "28px" }}
        />
      }
      btnText={"back"}
      btnType={"back"}
      showCreateBtn={false}
    />
    <Grid
      container
      component="form"
      id="form"
      className="px-[8px] items-baseline"
      rowGap={2.5}
      columnGap={4}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid container className="items-center">
        <Grid item xs={12} sm={6} className="text-start">
          <Typography
            className="font-Saira text-[15px] font-semibold"
            style={{ color: themeColors?.primary }}
          >
            {params?.id ? t("Debit Note") : t("New Debit Note")} {t("Details")}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} className="hide-on-print">
          <div className="flex gap-2 justify-end">
            <Button
              type={params?.id ? "button" : "submit"}
              disabled={IsBtnEnabled || loading}
              className="primary"
              style={{ background: themeColors?.primary }}
              variant="contained"
              disableElevation
              onClick={() =>
                params?.id
                  ? setConfirm(true)
                  : onSubmit(handleSubmit(onSubmit))
              }
            >
              {params?.id ? t("Authorize") : t("save")}
            </Button>
            {/* <Button
              className="secondary"
              variant="contained"
              style={{ color: themeColors?.primary }}
              disableElevation
              onClick={() => navigate(`${window.location.pathname}/print`)}
            >
              {t("Print")}
            </Button> */}
            <Button
              className="secondary"
              variant="contained"
              style={{ color: themeColors?.primary }}
              disableElevation
              onClick={() => navigate("/inventory/debit-note")}
            >
              {t("Back to list")}
            </Button>
          </div>
        </Grid>
      </Grid>
      {params?.id ? (
        <Grid
          container
          className="py-[12px] px-[18px] rounded-[8px] mb-[16px] items-center"
          style={{ background: themeColors?.primary }}
        >
          <Grid item xs={4}>
            <Typography className="font-Saira text-[15px] font-bold text-[#fff] text-left">
              {t("Debit Note")}
            </Typography>
            <Typography className="font-Saira text-[13px] text-[#fff] text-left">
              <span className="font-bold">{t("Debit Note")} # </span>{" "}
              <span>{creditnote?.creditDebitNoteNumber || ""}</span>
            </Typography>
            <Typography className="font-Saira text-[13px] text-[#fff] text-left">
              <span className="font-bold">{t("Date")}</span>{" "}
              <span>{creditnote?.creditDebitDate?.split("T")[0] || ""}</span>
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <div className="flex justify-end gap-10">
              <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                <span className="font-bold">
                  {t("Supplier")} {t("name")}
                </span>{" "}
                <br />
                <span className="font-medium">
                  {creditnote?.vendorTitle || ""}
                </span>
              </Typography>
              <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                <span className="font-bold">
                  {t("store") + " " + t("name")}
                </span>{" "}
                <br />
                <span className="font-medium">
                  {creditnote?.storeTitle || ""}
                </span>
              </Typography>
              <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                <span className="font-bold">{t("referenceNo")}</span> <br />
                <span className="font-medium">
                  {creditnote?.referenceNumber || ""}
                </span>
              </Typography>
              <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                <span className="font-bold">{t("Amount")}</span> <br />
                <span className="font-medium">
                  {new Intl.NumberFormat("en-US", {}).format(
                    (creditnote as SaveCreditNote)?.amount || 0
                  ) || 0}
                </span>
              </Typography>
            </div>
          </Grid>
        </Grid>
      ) : null}
      <Grid
        container
        item
        xs={12}
        gap={2.5}
        className="stockTransferDetails flex-nowrap"
      >
        <Grid item xs={12} md={4} lg={3}>
          <Controller
            name="purchaseInvoiceId"
            control={control}
            defaultValue={null}
            rules={{ required: t("This field is required.") }}
            render={({ field }) => (
              <DynamicSelect
                label={t("supplierBills") + " *"}
                defaultPlaceholder="phSelectSupplierBill"
                {...field}
                options={invoiceOptions}
                isSearchable
                isRequired={false}
                onChange={(selectedOption: any) => {
                  field.onChange(selectedOption);
                  reset(
                    { purchaseInvoiceId: selectedOption },
                    { keepValues: true },
                  );
                  setpCDNoteItems([]);
                  setpCDNoteService([]);
                  if (selectedOption?.value)
                    setTimeout(
                      () => getInvoiceById(selectedOption.value),
                      500,
                    );
                  if (!selectedOption?.value) reset();
                }}
                error={errors.purchaseInvoiceId?.message as string}
                isDisabled={params?.id as any}
                isLoading={searchLoading === "purchaseInvoices"}
                onInputChange={(
                  search: string,
                  { action }: { action: string },
                ) => {
                  if (
                    action === "input-change" ||
                    (action !== "set-value" &&
                      action !== "menu-close" &&
                      action !== "input-blur")
                  )
                    handleDropdownActions(
                      "purchaseInvoices",
                      "searchIt",
                      search,
                    );
                  else if (
                    action === "input-blur" ||
                    action === "menu-close"
                  ) {
                    if (dropdownSearchTerm || dropdownPage > 1)
                      handleDropdownActions(
                        "purchaseInvoices",
                        "searchIt",
                        "",
                      );
                    else setDropdownSearchTerm("");
                    setDropdownPage(1);
                  }
                }}
                onMenuScrollToBottom={() => {
                  if (!(invoiceData?.pages[0]?.data as any).last)
                    handleDropdownActions("purchaseInvoices", "loadNext");
                }}
                page={dropdownPage}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <TextField
            {...register("referenceNumber", {
              required: t("This field is required."),
            })}
            placeholder={t("phDebitNoteReferenceNo")}
            label={t("referenceNo") + " *"}
            title={t("referenceNo")}
            fullWidth
            error={!!errors.referenceNumber}
            helperText={errors.referenceNumber?.message as string}
            InputLabelProps={{ shrink: true }}
            disabled={params?.id as any}
            sx={{ opacity: params?.id ? 0.5 : 1 }}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <Controller
            name="creditDebitDate"
            control={control}
            rules={{
              validate: (value) => {
                const date = new Date(value);
                return isNaN(date.getTime()) ? t("Invalid Date") : true;
              },
            }}
            render={({ field }) => (
              <Box sx={{ opacity: params?.id ? 0.5 : 1 }}>
                <BasicDatePicker
                  type="datePicker"
                  {...field}
                  label={t("Debit Note") + " " + t("Date")}
                  fullWidth
                  onChange={(value: any) =>
                    field.onChange(
                      new Date(value)
                        .toLocaleDateString("en-CA", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                        })
                        ?.split("/")
                        .reverse()
                        .join("-"),
                    )
                  }
                  required={true}
                  error={errors.creditDebitDate?.message as string}
                  disable={(params as any)?.id}
                />
              </Box>
            )}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <Controller
            name="supplierId"
            control={control}
            defaultValue={null}
            rules={{ required: t("This field is required.") }}
            render={({ field }) => (
              <DynamicSelect
                label={t("vendors")}
                defaultPlaceholder="phSelectSupplier"
                {...field}
                options={
                  creditnote?.supplierId
                    ? [
                        {
                          value: creditnote?.supplierId,
                          label: creditnote?.vendorTitle,
                        },
                      ]
                    : []
                }
                isSearchable
                isRequired
                onChange={(selectedOption: any) => {
                  field.onChange(selectedOption);
                }}
                error={errors.supplierId?.message as string}
                value={
                  params?.id
                    ? {
                        value: creditnote?.supplierId,
                        label: creditnote?.vendorTitle,
                      }
                    : watch("supplierId")
                }
                isDisabled={params?.id as any}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <Controller
            name="storeId"
            control={control}
            defaultValue={null}
            rules={{ required: t("This field is required.") }}
            render={({ field }) => (
              <DynamicSelect
                label={t("stores")}
                defaultPlaceholder="phSelectWarehouse"
                {...field}
                options={
                  creditnote?.storeId
                    ? [
                        {
                          value: creditnote?.storeId,
                          label: creditnote?.storeTitle,
                        },
                      ]
                    : []
                }
                isSearchable
                isRequired
                onChange={(selectedOption: any) =>
                  field.onChange(selectedOption)
                }
                error={errors.storeId?.message as string}
                value={
                  params?.id
                    ? {
                        value: creditnote?.storeId,
                        label: creditnote?.storeTitle,
                      }
                    : watch("storeId")
                }
                isDisabled={params?.id as any}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} className="relative stockTransferDetails">
        {loading ? (
          <Loader />
        ) : (
          <>
            <Box className="bg-white py-2 px-[18px] rounded-[8px]">
              <Typography className="font-Saira text-[12px] font-semibold text-[#171826] text-left">
                {t("Debit Note")}
              </Typography>
              <InventoryItemsTable
                form={{
                  control,
                  register,
                  handleSubmit,
                  setValue,
                  errors,
                  watch,
                  resetField,
                }}
                columns={columns}
                handleInputChange={handleInputChange}
                rows={pCDNoteItems}
                typeName="pCDNoteItems"
                items={itemsData?.pages[0]?.data?.content || []}
                currency={loggedInUserCurrency}
                DeleteItem={DeleteItem}
                handleItem={handleItem}
                {...{
                  params,
                  dropdownPage,
                  searchLoading,
                  dropdownSearchTerm,
                  setDropdownSearchTerm,
                  setDropdownPage,
                }}
                checkStatus={
                  watch("purchaseInvoiceId")?.value && !params?.id
                    ? creditnote?.status
                    : 2
                }
                viewDelete={true}
                hideDelete={params?.id ? true : false}
                enableQuantity={true}
                itemsResponse={itemsData || {}}
                handleDropdownActions={handleDropdownActions}
              />
              <Grid item xs={12} sm={6} className="hide-on-print">
                <div className="flex gap-2 justify-start">
                  {pCDNoteItems.length < originalInvoiceItems.length && (
                    <Button
                      className="secondary flex gap-1"
                      style={{ color: themeColors?.primary }}
                      variant="contained"
                      disableElevation
                      onClick={() => addNewItem("pCDNoteItems")}
                    >
                      <AddIcon className="w-[20px] h-[20px]" />{" "}
                      {t("restoreRemovedParts")}
                    </Button>
                  )}
                </div>
              </Grid>
            </Box>
            <Box className="flex gap-2 justify-start mt-[16px]">
              <Button
                className="secondary cursor-text"
                style={{ color: themeColors?.primary }}
                variant="contained"
                disableElevation
              >
                {t("Additional Costs")}
              </Button>
            </Box>
            <Box className="bg-white py-2 px-[18px] rounded-[8px] mt-[16px]">
              <Box className="flex justify-between items-center">
                <Typography className="font-Saira text-[12px] font-semibold text-[#171826] text-left">
                  {t("Additional Cost")}
                </Typography>
                {checkPrivileges("ROLE_SERVICE_CREATE") && !params?.id ? (
                  <Button
                    className="secondary flex gap-1"
                    style={{ color: themeColors?.primary, padding: "10px" }}
                    variant="contained"
                    disableElevation
                    onClick={() => setAddNew("services")}
                  >
                    <AddIcon className="w-[15px] h-[15px]" />{" "}
                    {t("addNewService")}
                  </Button>
                ) : null}
              </Box>
              <InventoryServicesTable
                form={{
                  control,
                  register,
                  handleSubmit,
                  setValue,
                  errors,
                  watch,
                  resetField,
                }}
                columns={columnsService}
                handleInputChange={handleInputChange}
                rows={pCDNoteService}
                typeName="pCDNoteService"
                services={servicesData?.pages[0]?.data?.content || []}
                currency={loggedInUserCurrency}
                DeleteItem={DeleteItem}
                handleItem={handleItem}
                {...{ params }}
                checkStatus={params?.id ? 2 : 1}
                viewDelete={params?.id ? false : true}
                servicesResponse={servicesData || {}}
                handleDropdownActions={handleDropdownActions}
                {...{
                  dropdownPage,
                  searchLoading,
                  dropdownSearchTerm,
                  setDropdownSearchTerm,
                  setDropdownPage,
                }}
              />
              {params?.id ? null : (
                <Grid item xs={12} sm={6} className="hide-on-print">
                  <div className="flex gap-2 justify-start">
                    <Button
                      className="secondary flex gap-1"
                      style={{ color: themeColors?.primary }}
                      variant="contained"
                      disableElevation
                      onClick={() => addNewItem("pCDNoteService")}
                      disabled={params?.id as any}
                    >
                      <AddIcon className="w-[20px] h-[20px]" />{" "}
                      {t("Add More Items")}
                    </Button>
                  </div>
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider className="bg-[#AEB3C4] mt-[15px] mb-[8px]" />
              </Grid>
            </Box>
          </>
        )}
      </Grid>
      {addNew !== "" && (
        <FormDialog
          open={addNew !== ""}
          onClose={(event, reason) =>
            event && reason !== "backdropClick" ? setAddNew("") : ""
          }
          title={t('addService')}
          createPopup={true}
          fullWidth
          hideActions={true}
          content={<ServicesModal
            setAddNew={setAddNew}
            serviceRefetch={servicesRefetch}
          />}
        />
      )}
      <Typography className="font-Saira text-[11px] font-semibold text-[#171826] text-left mb-[-5px]">
        {t("Reason")}
      </Typography>
      <Grid container xs={12} spacing={1}>
        <Grid item xs={12} md={6}>
          <TextField
            {...register("reason")}
            placeholder={t("phDebitNoteReason")}
            title={t("Reason")}
            fullWidth
            error={!!errors.reason}
            helperText={errors.reason?.message as string}
            InputLabelProps={{ shrink: true }}
            multiline
            rows={5}
            sx={{
              "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                width: "100%",
              },
            }}
            disabled={(params as any)?.id}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            className="bg-[#D8D1E0] rounded-[8px]"
            sx={{
              border: "1px solid #5f6770",
              width: "calc(100% + 8px)",
              padding: "5px 14px",
            }}
          >
            <Box className="flex justify-end gap-12 mt-[10px]">
              <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[auto]">
                {t("Subtotal")}
              </Typography>
              <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[20%] text-end">
                <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                {new Intl.NumberFormat("en-US", {}).format(
                  totals.subTotal || 0
                )}
              </Typography>
            </Box>
            <Box className="flex justify-end gap-12 mt-[10px]">
              <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[auto]">
                {t("Discount")}
              </Typography>
              <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[20%] text-end">
                <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                {new Intl.NumberFormat("en-US", {}).format(
                  totals.discount || 0
                )}
              </Typography>
            </Box>
            <Box className="flex justify-end gap-12 mt-[10px]">
              <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[auto]">
                {t("tax")}{` (${taxPercentage}%)`}
              </Typography>
              <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[20%] text-end">
                <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                {new Intl.NumberFormat('en-US', {}).format((totals.taxableTotal * (taxPercentage / 100)) || 0)}
              </Typography>
            </Box>
            <Box className="flex justify-end gap-12 mt-[10px]">
              <Typography className="font-Saira text-[11px] text-[#3A3A3A] font-semibold w-[auto]">
                {t("totalWithTax")}
              </Typography>
              <Typography className="font-Saira text-[11px] text-[#3A3A3A] font-semibold w-[20%] text-end">
                <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                {/* {new Intl.NumberFormat("en-US", {}).format(
                  totals.total || (creditnote as SaveCreditNote)?.amount || 0
                )} */}
                {new Intl.NumberFormat("en-US", {}).format(
                  creditnote?.amount || (totals.total + ((totals.taxableTotal * taxPercentage) / 100)) || 0
                )}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid item xs={12} className="hide-on-print">
        <Divider className="bg-[#AEB3C4] mt-[8px]" />
      </Grid>
      <Grid container className="items-center hide-on-print">
        <Grid item xs={12} sm={6} className="text-start"></Grid>
        <Grid item xs={12} sm={6}>
          <div className="flex gap-2 justify-end">
            <Button
              type={params?.id ? "button" : "submit"}
              disabled={IsBtnEnabled || loading}
              className="primary"
              style={{ background: themeColors?.primary }}
              variant="contained"
              disableElevation
              onClick={() => params?.id ? setConfirm(true) : onSubmit(handleSubmit(onSubmit))}
            >
              {params?.id ? t('Authorize') : t('save')}
            </Button>
            {/* <Button
              className="secondary"
              variant="contained"
              style={{ color: themeColors?.primary }}
              disableElevation
              onClick={() => navigate(`${window.location.pathname}/print`)}
            >
              {t("Print")}
            </Button> */}
            <Button
              className="secondary"
              variant="contained"
              style={{ color: themeColors?.primary }}
              disableElevation
              onClick={() => navigate("/inventory/debit-note")}
            >
              {t("Back to list")}
            </Button>
          </div>
        </Grid>
      </Grid>
      {confirm &&
        <FormDialog
          open={confirm}
          onClose={(event, reason) => event && reason !== 'backdropClick' ? setConfirm(false) : ''}
          title={t('Authorize') + ' ' + t('Debit Note')}
          saveFunction={() => {
            InventoryProfileService.changeStatusEntity({ typeName: 'purchasecreditdebitnote', entityId: params?.id, status: 2 }).then((response: NewPurchaseOrderResponse) => {
              if (response.success) {
                setConfirm(false);
                navigate(`/inventory/debit-note/${response.data.id}/print`);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('Unstock') + ' ' + t('successfully')} />, { variant: 'success' });
              } else {
                setConfirm(false);
                enqueueSnackbar(<AlertMessages text={response?.message} />, { variant: 'error' });
              }
            }).catch((err: any) => {
              console.error("===", err);
              setConfirm(false);
              enqueueSnackbar(<AlertMessages text={err?.message || err?.data?.message || "Something went wrong"} />, { variant: 'error' });
            });
          }}
          content={
            <><Typography>{t('doYouWantUnstock')}</Typography></>
          }
          submitButtonText="OK"
        />
      }
    </Grid>
    </>
  );
};

export default CreditNoteCreateUpdate;
